import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const HowItWorksSection = () => {
  const { isRTL } = useLanguage();
  const connectorArrowIcon = isRTL ? 'fa-arrow-left' : 'fa-arrow-right';

  const steps = [
    {
      number: "1",
      icon: "fas fa-file-contract",
      title: isRTL ? "رفع العقد" : "Upload Contract",
      description: isRTL ? "FIDIC، عقود مخصصة، أو اتفاقيات الشراء المحلية." : "FIDIC, customized contracts, or local procurement agreements.",
      bgColor: "bg-green-500"
    },
    {
      number: "2",
      icon: "fas fa-balance-scale",
      title: isRTL ? "التحليل القانوني" : "Legal Analysis",
      description: isRTL ? "الذكاء الاصطناعي المدرب على معايير FIDIC وقوانين البناء في دول مجلس التعاون الخليجي يحلل المخاطر." : "AI trained on FIDIC standards and GCC construction laws analyzes risks.",
      bgColor: "bg-blue-500"
    },
    {
      number: "3",
      icon: "fas fa-exclamation-triangle",
      title: isRTL ? "تحديد المخاطر" : "Risk Identification",
      description: isRTL ? "تحديد النزاعات المحتملة ومسائل الامتثال قبل حدوثها." : "Identify potential disputes and compliance issues before they occur.",
      bgColor: "bg-purple-500"
    },
    {
      number: "4",
      icon: "fas fa-comments",
      title: isRTL ? "اسأل جيلاني الذكي" : "Ask Jelani AI",
      description: isRTL ? "احصل على إجابات فورية حول قانون البناء وبنود العقد." : "Get instant answers about construction law and contract clauses.",
      bgColor: "bg-orange-500",
      animation: "animate-pulse"
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4 max-w-2xl mx-auto">
            <p className="text-green-800 font-medium">
              {isRTL ? '🏗️ مشروعك لا ينام أبداً، نحن أيضاً - متاح 24/7' : '🏗️ Your project never sleeps, neither do we - 24/7 availability'}
            </p>
          </div>
        </div>
        
        <div className="relative">
          <div className="grid md:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <article
                  aria-label={step.title}
                  className="how-it-works-step text-center"
                >
                  <div className="relative mb-6">
                    <div className={`w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto text-white text-2xl shadow-lg`}>
                      <i
                        className={`${step.icon} ${step.animation || ''}`}
                        data-testid={`how-it-works-step-icon-${index + 1}`}
                      ></i>
                    </div>
                    <div className={`absolute -top-2 ${isRTL ? '-left-2' : '-right-2'} w-8 h-8 ${step.bgColor} rounded-full flex items-center justify-center text-white text-xs font-bold`}>
                      {step.number}
                    </div>
                  </div>
                  <h3 className={`text-lg font-semibold text-gray-900 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>{step.title}</h3>
                  <p className={`text-gray-600 ${isRTL ? 'text-right' : 'text-left'}`}>{step.description}</p>
                </article>
                
                {/* Arrow between steps (not for the last step) */}
                {index < steps.length - 1 && (
                  <div className={`hidden md:block absolute top-10 ${isRTL ? 'left-0 -translate-x-1/2' : 'right-0 translate-x-1/2'} transform -translate-y-1/2 z-10`}>
                    <div
                      className="flex items-center"
                      data-testid="how-it-works-connector-track"
                      dir="ltr"
                    >
                      <div className={`w-8 h-0.5 bg-gradient-to-r from-blue-400 to-blue-600 ${isRTL ? 'order-2' : ''}`}></div>
                      <div className={`${isRTL ? 'order-1 mr-1' : 'ml-1'} w-3 h-3 bg-blue-600 rounded-full animate-pulse`}></div>
                      <div className={`${isRTL ? 'order-0 mr-1' : 'ml-1'} text-blue-600`}>
                        <i
                          className={`fas ${connectorArrowIcon} text-lg animate-bounce`}
                          data-testid="how-it-works-connector-arrow"
                          style={{animationDelay: `${index * 0.2}s`}}
                        ></i>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        
        <div className="mt-16 text-center">
          <button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 transition hover-lift"
          >
            {isRTL ? 'شاهد العرض التوضيحي المباشر' : 'See Live Demo'}
            <i className={`fas ${isRTL ? 'fa-arrow-left mr-2' : 'fa-arrow-right ml-2'}`}></i>
          </button>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
